import React, { useEffect, useState } from 'react';
import { Alert, Card, Space, Spin, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { vendorApi } from '@/utils/vendorApi';
import { useAuthStore } from '@/store/authStore';
import { canEnterTransfers } from '@/store/transferAccess';
import { fileNameFromTransferId, transferStatusDisplay } from '@/components/transfer/transferStatus';
import { matchesSelectedBu } from '@/utils/businessUnits';
import { TransferItem, TransferListResponse } from '@/types';

const { Title, Text } = Typography;

function formatTime(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY HH:mm') : '—';
}

function mergeTransfers(rows: TransferItem[]): TransferItem[] {
  const sorted = [...rows].sort((left, right) =>
    (right.lastTransitionAt || '').localeCompare(left.lastTransitionAt || '')
  );
  const seen = new Set<string>();
  const unique: TransferItem[] = [];
  for (const row of sorted) {
    if (seen.has(row.transferId)) {
      continue;
    }
    seen.add(row.transferId);
    unique.push(row);
  }
  return unique;
}

const FailedTransfersCard: React.FC = () => {
  const { authorization, currentBusinessUnit } = useAuthStore();
  const [rows, setRows] = useState<TransferItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [partial, setPartial] = useState(false);
  const [moreAvailable, setMoreAvailable] = useState(false);

  useEffect(() => {
    const current = useAuthStore.getState().authorization;
    if (!canEnterTransfers(current)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const [failedResult, exhaustedResult] = await Promise.allSettled([
        vendorApi.listTransfers({ status: 'failed', limit: 25 }),
        vendorApi.listTransfers({ status: 'retries_exhausted', limit: 25 }),
      ]);
      if (cancelled) {
        return;
      }
      const succeeded: TransferListResponse[] = [];
      if (failedResult.status === 'fulfilled') {
        succeeded.push(failedResult.value);
      }
      if (exhaustedResult.status === 'fulfilled') {
        succeeded.push(exhaustedResult.value);
      }
      if (succeeded.length === 0) {
        setFailed(true);
        setRows([]);
        setLoading(false);
        return;
      }
      setPartial(succeeded.length === 1);
      setMoreAvailable(succeeded.some((result) => Boolean(result.nextToken)));
      setRows(mergeTransfers(succeeded.flatMap((result) => result.transfers)));
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedId = currentBusinessUnit?.id;
  const visible = (selectedId
    ? rows.filter((row) => matchesSelectedBu(row.bu, selectedId, authorization?.catalog))
    : rows
  ).slice(0, 5);

  return (
    <Card>
      <Title level={4} style={{ marginTop: 0 }}>Transfers that need attention</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Latest failed transfers</Text>
      {loading ? (
        <Spin />
      ) : failed ? (
        <Alert type="error" showIcon message="Couldn't load transfers." />
      ) : (
        <>
          {partial && (
            <Alert
              type="warning"
              showIcon
              message="Couldn't load every transfer."
              style={{ marginBottom: 12 }}
            />
          )}
          {visible.length === 0 ? (
            <Text type="secondary">No failed transfers in the latest results.</Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {visible.map((row) => {
                const fileName = fileNameFromTransferId(row.transferId);
                const status = transferStatusDisplay(row.status);
                return (
                  <div key={row.transferId}>
                    <Text>{row.vendorId || '—'}</Text>
                    <Text style={{ marginLeft: 8 }}>{fileName || '—'}</Text>
                    <Tag color={status.color} style={{ marginLeft: 8 }}>{status.label}</Tag>
                    <Text type="secondary" style={{ marginLeft: 8 }}>{formatTime(row.lastTransitionAt)}</Text>
                  </div>
                );
              })}
            </Space>
          )}
          {moreAvailable && (
            <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              More may be available in Transfers.
            </Text>
          )}
        </>
      )}
      {!loading && (
        <Space style={{ marginTop: 16 }}>
          <Link to="/transfers?status=failed">View failed</Link>
          <Link to="/transfers?status=retries_exhausted">View retries exhausted</Link>
        </Space>
      )}
    </Card>
  );
};

export default FailedTransfersCard;
